import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';

import {
    chatKeys,
    generateUUID,
    useAgentChats,
    useChatMessages,
    useSendMessageV2,
    useStartAgentChat,
} from '@codes/chatbots';
import { useWebCoreStore } from '@codes/web-core';

import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

interface UseAgentChatProps {
    agentId: string;
}

interface ChatViewWithError extends ChatView {
    isError?: boolean;
}

export const useAgentChat = ({ agentId }: UseAgentChatProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { profile } = useWebCoreStore();

    // State management
    const [currentChat, setCurrentChat] = useState<ChatView | null>(null);
    const [messages, setMessages] = useState<ChatView[]>([]);
    const [pendingMessage, setPendingMessage] = useState<ChatViewWithError | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);
    const [input, setInput] = useState('');
    const initializedRef = useRef(false);

    // Data fetching
    const { data: agentChatsData, isLoading: chatsLoading } = useAgentChats({ view: 'mine', stereo: 'root', agentId });
    const { data: messagesData, isLoading: isMessagesLoading } = useChatMessages({
        rootId: currentChat?.id,
        limit: -1,
    });

    const startMyChat = useStartAgentChat();
    const sendMessage = useSendMessageV2();

    // Load messages from current chat
    useEffect(() => {
        if (messagesData?.pages) {
            const allMessages = messagesData.pages.reduce<ChatView[]>((acc, page) => {
                return [...acc, ...(page.data || [])];
            }, []);

            // Process documentIds from search messages before filtering
            const processedMessages = allMessages.map((msg, index) => {
                if (msg.stereo === 'answer' && !msg.documentIds) {
                    // Look for the most recent search message before this answer
                    for (let i = index - 1; i >= 0; i--) {
                        const prevMessage = allMessages[i];
                        if (prevMessage.stereo === 'search' && prevMessage.documentIds) {
                            return { ...msg, documentIds: prevMessage.documentIds };
                        }
                    }
                }
                return msg;
            });

            const filteredMessages = processedMessages.filter(msg => msg.id && msg.content && !msg.hidden);
            const sortedMessages = filteredMessages.sort((chatA, chatB) => {
                const aOrderNo = chatA.childNo ?? chatA.no ?? 0;
                const bOrderNo = chatB.childNo ?? chatB.no ?? 0;
                return aOrderNo - bOrderNo;
            });

            setMessages(sortedMessages);
        }
    }, [messagesData]);

    // Initialize chat
    const initializeChat = useCallback(async () => {
        if (!currentChat && profile && agentId && !initializedRef.current && !chatsLoading) {
            try {
                initializedRef.current = true;
                setIsLoading(true);

                if (agentChatsData?.data && agentChatsData.data.length > 0) {
                    // Use existing chat
                    setCurrentChat(agentChatsData.data[0]);
                } else {
                    // Create new chat
                    const chatName = `Chat with Agent ${agentId}`;
                    const profile$ = {
                        ...(profile?.sid && { sid: profile.sid }),
                        ...(profile?.uid && { uid: profile.uid }),
                        ...(profile?.$user?.gender && { gender: profile?.$user?.gender }),
                        ...(profile?.$user?.name && { name: profile?.$user?.name }),
                    };

                    const newChat = await startMyChat.mutateAsync({ name: chatName, profile$, agentId });
                    setCurrentChat(newChat);
                }
            } catch (error) {
                console.error('Failed to start chat:', error);
                toast({ title: t('chat.create_new_chat_failed') });
                initializedRef.current = false;
            } finally {
                setIsLoading(false);
            }
        }
    }, [profile, agentId, startMyChat, t, agentChatsData?.data, chatsLoading, currentChat]);

    useEffect(() => {
        initializeChat();
    }, [initializeChat]);

    // Message handling
    const clearPendingMessage = useCallback(() => {
        setPendingMessage(null);
        setIsWaitingResponse(false);
    }, []);

    const addMessage = useCallback(
        async (messageContent: string) => {
            if (!messageContent.trim() || !currentChat?.id || isWaitingResponse) {
                return;
            }

            const userMessage: ChatViewWithError = {
                id: generateUUID(),
                content: messageContent.trim(),
                stereo: 'query',
                createdAt: Date.now(),
                childNo: (messages.length + 1) * 10,
            };

            setPendingMessage(userMessage);
            setIsWaitingResponse(true);
            setInput('');

            try {
                const res = await sendMessage.mutateAsync({
                    groupId: currentChat.id!,
                    body: {
                        ...(profile?.sid && { sid: profile.sid }),
                        content: messageContent.trim(),
                    },
                });

                await updateChatMessages(currentChat.id, res.list || []);
                clearPendingMessage();
            } catch (error) {
                console.error('Failed to send message:', error);
                setPendingMessage(prev => (prev ? { ...prev, isError: true } : null));
                setIsWaitingResponse(false);
                toast({ title: t('chat.send_message_failed') });
            }
        },
        [currentChat, messages.length, sendMessage, profile, clearPendingMessage, t, isWaitingResponse]
    );

    // Update chat messages in cache
    const updateChatMessages = async (chatId: string, newMessages: ChatView[]) => {
        await queryClient.setQueryData(chatKeys.list({ rootId: chatId, limit: -1 }), (oldData: any) => {
            if (!oldData) {
                return oldData;
            }

            const newFirstPage = {
                ...oldData.pages[0],
                data: [...(oldData.pages[0].data || []), ...newMessages],
            };
            return {
                ...oldData,
                pages: [newFirstPage, ...oldData.pages.slice(1)],
                pageParams: oldData.pageParams,
            };
        });
    };

    // Computed values
    const displayMessages = useMemo(() => {
        const allMessages = [...messages];
        if (pendingMessage) {
            allMessages.push(pendingMessage);
        }
        return allMessages
            .filter(chat => !chat.hidden && (chat.stereo === 'query' || chat.stereo === 'answer'))
            .sort((chatA, chatB) => {
                const aOrderNo = chatA.childNo ?? chatA.no ?? 0;
                const bOrderNo = chatB.childNo ?? chatB.no ?? 0;
                return aOrderNo - bOrderNo;
            });
    }, [messages, pendingMessage]);

    return {
        currentChat,
        displayMessages,
        input,
        setInput,
        addMessage,
        isLoading,
        isWaitingResponse,
        isMessagesLoading,
    };
};
